import type { FormValues } from "components/Deal-amortization/schemas";
import dayjs from "dayjs";
import { groupBy, last } from "lodash";
import { DebtSegment } from "store/types";

// Group segments by deal_id and get the last segment from each group (representing the latest withdrawal)
export const getLastSegmentsByDealId = (segments: DebtSegment[]): DebtSegment[] => {
  const segmentsByDealId = groupBy(segments, "deal_id");
  return Object.values(segmentsByDealId).map((segments) => last(segments)!);
};

export const getFacilitySegment = (segments: DebtSegment[]): DebtSegment | undefined => {
  return segments.find((segment) => segment.is_facility);
};

export const getWithdrawalSegments = (segments: DebtSegment[]): DebtSegment[] => {
  return segments.filter((segment) => segment.is_withdrawal);
};

export const getSegmentsOfDeal = (segments: DebtSegment[], dealId: number): DebtSegment[] => {
  return segments.filter((segment) => segment.deal_id === dealId);
};

/**
 * Default values for a new facility or withdrawal
 */
export const defaultFacilityValues: FormValues = {
  id: 0,
  deal_id: 0,

  // Payment Terms
  payment_term: "fixed_principal",
  overriding_parent: false,
  is_facility: true,
  is_withdrawal: false,

  // Debt section
  deal_title: "",
  loan_amount: 0,
  start_date: dayjs().format(),
  maturity_date: dayjs().format(),

  // Principal
  principal_frequency: "month",
  principal_first_payment_date: null,
  principal_reference_index: null,
  principal_reference_day: null,
  principal_link_percent: 0,
  principal_index_value: null,
  balloon_payment: null,
  custom_payments_file: null,
  principal_indexation_payment: false,
  interest_based_on_principal: false,

  // Interest
  interest_frequency: "month",
  interest_first_payment_date: null,
  interest_reference_index: null,
  interest_reference_day: null,
  interest_reference_day_offset: null,
  interest_link_percent: 0,
  interest_index_value: null,
  base_rate: null,
  ceiling_rate: null,
  floor_rate: 0,
  float_type: "fixed",
  interest_rate_fixing: null,
  interest_accrual_method: "actual_actual",
  commission_rate: null,

  loan_status_frequency: "quarter",
  loan_first_status_date: null,

  // PIK
  pik_base_rate: null,
  pik_capitalization_frequency: "year",
  pik_first_capitalization_date: null,
  pik_reference_index: null,
  pik_reference_day_offset: null,
  pik_reference_day: null,
  pik_link_percent: 0,
  pik_index_value: null,
  pik_floor_rate: 0,
  pik_ceiling_rate: null,
  pik_float_type: "fixed",
  pik_rate_fixing: null,
  pik_accumulated: null,

  // VAT
  vat: null,

  // Fees
  fees: []
};

interface GetDisabledFieldsParams {
  paymentTerm: string;
  isWithdrawal: boolean;
  isFacility: boolean;
  overridingParent: boolean;
}

/**
 * Determines which fields should be disabled based on facility configuration
 *
 * @param paymentTerm - The payment term type (balloon, partial_balloon, etc.)
 * @param isWithdrawal - Whether this is a withdrawal (not a facility)
 * @param isFacility - Whether this is a facility
 * @param overridingParent - Whether the withdrawal is overriding parent settings
 * @returns Array of field names that should be disabled
 */
export function getDisabledFields({
  paymentTerm,
  isWithdrawal,
  isFacility,
  overridingParent
}: GetDisabledFieldsParams): string[] {
  const disabledFields: string[] = [];

  // Disable principal fields for balloon/partial balloon (only paid at maturity)
  if (["balloon", "partial_balloon"].includes(paymentTerm)) {
    disabledFields.push("principal_first_payment_date", "principal_frequency", "balloon_payment");
  }

  // Disable interest fields only for balloon (not partial balloon)
  if (paymentTerm === "balloon") {
    disabledFields.push(
      "interest_reference_index",
      "interest_index_value",
      "interest_link_percent",
      "interest_reference_day",
      "interest_frequency",
      "base_rate",
      "floor_rate",
      "ceiling_rate",
      "float_type",
      "interest_rate_fixing",
      "interest_accrual_method",
      "commission_rate",
      "interest_first_payment_date"
    );
  }

  // Pure withdrawal (not single-payment) without overriding parent - most fields disabled
  if (isWithdrawal && !isFacility && !overridingParent) {
    disabledFields.push(
      // Debt fields (except loan_amount, start_date, maturity_date)
      "numberOfMonths",
      "maturity_date",
      "vat",
      // Principal fields (except principal_first_payment_date)
      "principal_reference_index",
      "principal_index_value",
      "principal_link_percent",
      "principal_reference_day",
      "principal_frequency",
      "balloon_payment",
      "custom_payments_file",
      // Interest fields (except interest_first_payment_date)
      "interest_reference_index",
      "interest_index_value",
      "interest_link_percent",
      "interest_reference_day_offset",
      "interest_frequency",
      "base_rate",
      "floor_rate",
      "ceiling_rate",
      "float_type",
      "interest_rate_fixing",
      "interest_accrual_method",
      "commission_rate",
      // PIK fields (all disabled)
      "pik_base_rate",
      "pik_capitalization_frequency",
      "pik_reference_index",
      "pik_reference_day_offset",
      "loan_status_frequency",
      "pik_index_value",
      "pik_link_percent",
      "pik_floor_rate",
      "pik_float_type",
      "pik_rate_fixing",
      "pik_accumulated"
    );
  } else if (isFacility && !isWithdrawal) {
    // Pure facility (not single-payment) - disable first payment date fields
    disabledFields.push(
      "principal_first_payment_date",
      "balloon_payment",
      "interest_first_payment_date",
      "pik_first_capitalization_date",
      "loan_first_status_date",
      "interest_reference_day",
      "pik_reference_day"
    );
  }

  return disabledFields;
}

/**
 * Calculates the end of period date based on start date and frequency
 * @param start - The start date (dayjs.Dayjs)
 * @param frequency - The frequency (month, quarter, semi-annual, year)
 * @returns ISO string representing the end of the period
 */
export function getEndOfPeriod(start: dayjs.Dayjs, frequency: string): string {
  switch (frequency) {
    case "month":
      return start.endOf("month").format();
    case "quarter": {
      // Calculate quarter end: Q1=Mar, Q2=Jun, Q3=Sep, Q4=Dec
      const month = start.month();
      const quarterEndMonth = Math.floor(month / 3) * 3 + 2; // 2, 5, 8, or 11
      return start.month(quarterEndMonth).endOf("month").format();
    }
    case "semi-annual": {
      // Semi-annual: if in H1 (Jan-Jun), end of June; if in H2 (Jul-Dec), end of December
      const month = start.month();
      if (month < 6) {
        return start.month(5).endOf("month").format();
      } else {
        return start.endOf("year").format();
      }
    }
    case "year":
      return start.endOf("year").format();
    default:
      return start.endOf("month").format();
  }
}
